import { useCampaignData, useJobillaOrgId } from '@/hooks'
import {
	CampaignOverview,
	CampaignOverviewViewModel,
	ConformedCampaignFeedAd,
	ConformedCampaignStoryAd,
	conformFeedAd,
	conformStoryAd,
	logger,
} from '@/ui'
import {
	campaignChannel,
	campaignChannelMethods,
	campaignsShowQueryKey,
	useCampaignChannelDestroy,
	useCampaignChannelUpdate,
	useCampaignInvoiceStatus,
	useCampaignsShow,
	useOrganisationRelationShow,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'

export const Route = createFileRoute(
	'/_auth/company/$companyId/$campaignId/overview',
)({
	component: RouteComponent,
})

function RouteComponent() {
	const jobillaOrgId = useJobillaOrgId()
	const queryClient = useQueryClient()
	const { campaignId, companyId } = Route.useParams()
	const { campaign, isLoading } = useCampaignData({ campaignId })
	const { data: invoiceData, isLoading: isLoadingInvoice } = useCampaignInvoiceStatus(
		jobillaOrgId,
		campaignId,
	)
	const invoice = invoiceData?.invoice ?? undefined
	const payments = invoiceData?.payments ?? undefined

	const rawCampaign = useCampaignsShow(campaignId)

	// Career site toggle state
	const orgQuery = useOrganisationRelationShow(jobillaOrgId, { enabled: !!jobillaOrgId })
	const careerSiteEnabled = orgQuery.data?.careerSiteEnabled ?? false
	const careerSiteVisible = rawCampaign.data?.careerSiteVisible ?? false
	const campaignChannels = rawCampaign.data?.channels ?? [campaignChannel.META]
	const isCareerSiteOnlyChannel = campaignChannels.length === 1 && campaignChannels[0] === campaignChannel.CAREER_SITE
	const campaignStatus = rawCampaign.data?.status
	const canChangeCareerSite = !rawCampaign.data?.isArchived && (
		campaignStatus === 'draft'
		|| ((campaignStatus === 'review' || campaignStatus === 'active')
			&& campaignChannelMethods.isTogglableWhileLive(campaignChannel.CAREER_SITE))
	)
	const invalidateCampaign = () => {
		void queryClient.invalidateQueries({ queryKey: campaignsShowQueryKey(campaignId) })
	}
	const enableChannelMutation = useCampaignChannelUpdate({ onSuccess: invalidateCampaign })
	const disableChannelMutation = useCampaignChannelDestroy({ onSuccess: invalidateCampaign })

	const welcomeScreenMeta = rawCampaign.data?.welcomeScreen?.meta?.__ui as
		| { reachOverrideEnabled?: boolean; reachOverrideUrl?: string }
		| undefined
	const reachOverride = (() => {
		if (!welcomeScreenMeta?.reachOverrideEnabled || !welcomeScreenMeta?.reachOverrideUrl) {
			return undefined
		}
		try {
			new URL(welcomeScreenMeta.reachOverrideUrl as string)
			return { url: welcomeScreenMeta.reachOverrideUrl as string }
		} catch {
			return undefined
		}
	})()

	// TODO: refactor this to a hook; it's used in -useNewCampaignFeedEditor, too.
	const feed = rawCampaign.data?.feed
	const feedAd: ConformedCampaignFeedAd | undefined = feed && feed.id
		? conformFeedAd({
			id: feed.id,
			title: feed.title ?? '',
			subtitle: feed.subtitle ?? '',
			location: feed.location ?? '',
			primaryText: feed.metaPrimaryText ?? '',
			headlineText: feed.metaTitle ?? '',
			descriptionText: feed.metaDescription ?? '',
			ctaText: feed.button ?? '',
			metaCtaText: feed.metaCallToAction ?? '',
			imageId: feed.imageId ?? '',
			meta: feed.meta?.__ui ?? {},
		})
		: undefined

	const story = rawCampaign.data?.story
	const storyAd: ConformedCampaignStoryAd | undefined = story && story.id
		? conformStoryAd({
			id: story.id,
			title: story.title ?? '',
			subtitle: story.subtitle ?? '',
			location: story.location ?? '',
			ctaText: story.button ?? '',
			imageId: story.imageId ?? '',
			meta: story.meta?.__ui ?? {},
		})
		: undefined

	const formPreviewLink: CampaignOverviewViewModel['renderers']['formPreviewLink'] = ({ campaignId, ...props }) => (
		<a
			href={import.meta.env.VITE_FORM_URL + campaign.formId + '?reference'}
			target="_blank"
			onClick={() => {
				logger.track('Campaign Card: Previewed Form', {
					data: {
						campaign_status: campaign.status,
						campaign_id: campaignId,
						organisation_id: jobillaOrgId,
					},
				})
			}}
		>
			{props.children ?? null}
		</a>
	)

	// Only show edit form button for review and active campaigns when slug is available
	const canEditForm = ['review', 'active'].includes(campaign.status)

	const formEditorLink: CampaignOverviewViewModel['renderers']['formEditorLink'] = canEditForm
		? ({ campaignId, ...props }) => (
			<Link
				to="/company/$companyId/$campaignId/form-editor"
				params={{ companyId, campaignId }}
				onClick={() => {
					logger.track('Campaign Card: Opened Form Editor', {
						data: {
							campaign_status: campaign.status,
							campaign_id: campaignId,
							organisation_id: jobillaOrgId,
						},
					})
				}}
			>
				{props.children ?? null}
			</Link>
		)
		: undefined

	const careerSite = careerSiteEnabled
		? {
			visible: careerSiteVisible,
			onChange: (visible: boolean) => {
				const variables = { campaign: campaignId, channel: campaignChannel.CAREER_SITE }
				if (visible) {
					disableChannelMutation.reset()
					enableChannelMutation.mutate(variables)
				} else {
					enableChannelMutation.reset()
					disableChannelMutation.mutate(variables)
				}
			},
			disabled: !canChangeCareerSite
				|| (careerSiteVisible && isCareerSiteOnlyChannel)
				|| enableChannelMutation.isPending
				|| disableChannelMutation.isPending,
			isOnlyChannel: isCareerSiteOnlyChannel,
			hasError: enableChannelMutation.isError || disableChannelMutation.isError,
		}
		: undefined

	return (
		<CampaignOverview
			view={{ campaign, feedAd, storyAd, invoice, payments, reachOverride, careerSite }}
			loading={{ campaign: isLoading, invoice: isLoadingInvoice }}
			events={{}}
			renderers={{
				formPreviewLink: reachOverride ? undefined : formPreviewLink,
				formEditorLink: reachOverride ? undefined : formEditorLink,
			}}
		/>
	)
}
